import styled from '@emotion/styled';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

// Components
import Checkbox from './oc-checkbox';
import InputError from './oc-input-error';
import Label from './oc-label';
import Theme from '../../../themes/index';

const Container = styled.div`
  display: inline-block;
  position: relative;
  margin-bottom: ${props => props.margin};
  width: 100%;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media screen and (max-width: 767px) {
    background-color: #ffffff;
    border-left: 1px solid #000000;
    border-right: 1px solid #000000;
    border-top: 1px solid #000000;
  }

  @media screen and (min-width: 768px) {
    flex-direction: ${props => (props.vertical ? 'column' : 'row')};
  }

  label {
    margin-bottom: 0.5em;

    ${props =>
      !props.vertical &&
      `
      margin-right: 4em;
    `}

    &:last-of-type {
      margin-right: 0;
    }

    @media screen and (max-width: 767px) {
      border-bottom: 1px solid #000000;
      margin: 0;
      padding: 16px 8px;

      input[type='checkbox'] ~ .check {
        left: 10px;
        top: 21px;
      }
    }
  }
`;

const CheckboxContainer = styled.div`
  display: inline-block;
  width: 100%;
`;

const Error = styled(InputError)`
  display: inline-block;
  width: 100%;
`;

const CheckboxGroup = ({
  error,
  helperText,
  infoFunction,
  label,
  margin = '40px',
  name,
  themeColor = Theme.colors.brand,
  onBlur,
  onChange,
  options,
  required = false,
  valid = true,
  vertical = true,
  ...props
}) => {
  const [values, setValues] = useState([]);

  // Note: This handler is currently specific to the FormWrapper component validation function
  const handleChange = event => {
    const selectedValue = event.target.value;
    const newValues = [...values];

    if (newValues.includes(selectedValue)) {
      _.remove(newValues, value => value == selectedValue);
    } else {
      newValues.push(selectedValue);
    }

    setValues(newValues);
    onChange(event, { ...props }, name, newValues);
  };

  return (
    <Container label={label} margin={margin}>
      {label && (
        <Label text={label} helperText={helperText} valid={valid} infoFunction={infoFunction} required={required} />
      )}

      <Group vertical={vertical} onBlur={onBlur} onChange={handleChange}>
        {options.map((option, optionIndex) => (
          <Checkbox key={`option-${optionIndex}`} name={name} themeColor={themeColor} value={option.value}>
            {option.text}
          </Checkbox>
        ))}
      </Group>

      {error && !valid && (
        <CheckboxContainer>
          <Error message={error} />
        </CheckboxContainer>
      )}
    </Container>
  );
};

export default CheckboxGroup;

CheckboxGroup.propTypes = {
  /* HTML: (Optional) Text string to display as a top level label for group or selections. */
  label: PropTypes.string,
  /* HTML: (Optional) Text string to display after the top level label. */
  helperText: PropTypes.string,
  /* HTML: Name attribute for the individual input elements. */
  name: PropTypes.string,
  /* CSS: Hex value for color theme */
  themeColor: PropTypes.string,
  /* JSX: (Required) Option objects that render the actual radio input elements; should at minimum include text and value props. */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /* CSS: (Optional) String px value to adjust the margin-bottom value for the group. */
  margin: PropTypes.string,
  /* CSS: Set to true if fields should be listed vertically instead of inline. */
  vertical: PropTypes.bool,
};
